import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import TriExpressMode from './TriExpressMode';
import { playGoldSound } from '../hooks/useGoldSound';

const API = `${(process.env.REACT_APP_BACKEND_URL || 'https://bobine.live')}/api`;
const serif = "'Georgia', 'Times New Roman', serif";
const gold = '#D4AF37';
const ivory = '#FAF3E0';
const ivoryAlpha = (a) => `rgba(245,245,220,${a})`;

const SasOverview = ({ token, sasCount, onToutSceller, onRefreshAfterSeal, onSmartTamis, onTriManuel, onClose, sounds, onAlbumPhotoAdded }) => {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [sealing, setSealing] = useState(false);

  // Progressive analysis
  const [inspecting, setInspecting] = useState(false);
  const [doublonsResult, setDoublonsResult] = useState(null);
  const [facesResult, setFacesResult] = useState(null);
  const [facesLoading, setFacesLoading] = useState(false);
  const [namingGroup, setNamingGroup] = useState(null);
  const [nameInput, setNameInput] = useState('');

  // Seal modal
  const [showSealModal, setShowSealModal] = useState(false);
  const [sealEcrinName, setSealEcrinName] = useState('');
  const [sealPhotoIds, setSealPhotoIds] = useState(null);
  const [sealSuggestion, setSealSuggestion] = useState(null);

  // Merge
  const [mergeMode, setMergeMode] = useState(false);
  const [mergeSelected, setMergeSelected] = useState(new Set());
  const [merging, setMerging] = useState(false);

  // Excluded photos (user can remove before sealing)
  const [excludedIds, setExcludedIds] = useState(new Set());

  // Selection mode for scelling
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Tri Express
  const [triExpressOpen, setTriExpressOpen] = useState(false);
  const [albums, setAlbums] = useState([]);

  useEffect(() => {
    if (!triExpressOpen) return;
    fetch(`${API}/albums/list`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => setAlbums(Array.isArray(data) ? data : (data.albums || [])))
      .catch(() => setAlbums([]));
  }, [triExpressOpen, token]);

  const toggleSelectPhoto = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Manual linking
  const [linkingPhotoId, setLinkingPhotoId] = useState(null);
  const [newPersonName, setNewPersonName] = useState('');
  const [linkingInProgress, setLinkingInProgress] = useState(false);

  // Doublon Coffre detection
  const [coffreDuplicates, setCoffreDuplicates] = useState(null);
  const [checkingDoublons, setCheckingDoublons] = useState(false);

  // Scan progress
  const [scanProgress, setScanProgress] = useState(null);

  // Mobile : rendu progressif des vignettes pour éviter de geler le thread JS
  // (124 photos × IntersectionObserver simultané = freeze sur mobile)
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const INITIAL_CHUNK = isMobile ? 24 : 60;
  const CHUNK_STEP = isMobile ? 24 : 60;
  const [renderCount, setRenderCount] = useState(INITIAL_CHUNK);

  const headers = { Authorization: `Bearer ${token}` };

  const loadPhotos = useCallback(() => {
    setLoadError(false);
    setLoading(true);
    axios.get(`${API}/sas/photos`, { headers, timeout: 30000 })
      .then(res => { setPhotos(res.data.photos || []); setLoading(false); })
      .catch(() => { setLoadError(true); setLoading(false); });
  }, [token]);

  // NON-BLOCKING inspection: show page immediately, scans run in background
  const handleInspect = useCallback(async (silent = false) => {
    // Load existing face groups INSTANTLY (from DB)
    try {
      const groupRes = await axios.get(`${API}/faces/groups?status=sas`, { headers, timeout: 5000 }).catch(() => null);
      if (groupRes?.data?.groups?.length > 0) setFacesResult(groupRes.data);
    } catch {}

    // Background scans (non-blocking)
    // En mode silencieux (auto-inspection), on n'allume pas le spinner du bouton :
    // la barre de progression dorée discrète au-dessus suffit comme retour visuel.
    if (!silent) setFacesLoading(true);

    // Doublons — use cached filtered results only (scan runs too slow)
    axios.get(`${API}/doublons/results`, { headers, timeout: 8000 })
      .then(res => { setDoublonsResult(res.data); })
      .catch(() => {});

    // Faces in background — loop until all scanned (delayed to let SAS load first)
    setTimeout(() => {
    const scanLoop = async () => {
      // Get initial status
      try {
        const statusRes = await axios.get(`${API}/faces/scan-status`, { headers });
        setScanProgress({ scanned: statusRes.data.scanned_photos || 0, total: statusRes.data.total_photos || 0 });
      } catch {}

      let keepGoing = true;
      let totalScannedThisSession = 0;
      while (keepGoing) {
        try {
          const scanRes = await axios.post(`${API}/faces/scan`, {}, { headers, timeout: 55000 });
          const scanned = scanRes.data?.photos_scanned || 0;
          totalScannedThisSession += scanned;
          // Update progress
          try {
            const statusRes = await axios.get(`${API}/faces/scan-status`, { headers });
            setScanProgress({ scanned: statusRes.data.scanned_photos || 0, total: statusRes.data.total_photos || 0 });
          } catch {}
          if (scanned === 0) { keepGoing = false; break; }
          // Group + refresh after each batch
          await axios.post(`${API}/faces/group`, {}, { headers, timeout: 30000 }).catch(() => {});
          const groupRes = await axios.get(`${API}/faces/groups?status=sas`, { headers });
          setFacesResult(groupRes.data);
        } catch { keepGoing = false; }
      }
      // Final group + refresh
      try {
        await axios.post(`${API}/faces/group`, {}, { headers, timeout: 30000 });
        const groupRes = await axios.get(`${API}/faces/groups?status=sas`, { headers });
        setFacesResult(groupRes.data);
      } catch {}
      setScanProgress(prev => prev ? { ...prev, scanned: prev.total } : null);
      setFacesLoading(false);
      // "Pluie d'or" — uniquement si le scan a effectivement traité des photos cette session
      if (totalScannedThisSession > 0) {
        try { playGoldSound(); } catch {}
      }
    };
    scanLoop();
    }, 3000);
  }, [token]);

  // Auto-load + auto-inspect (non-blocking)
  const autoInspectDone = React.useRef(false);
  useEffect(() => { loadPhotos(); }, [loadPhotos]);
  useEffect(() => {
    if (!loading && photos.length > 0 && !autoInspectDone.current) {
      autoInspectDone.current = true;
      // Délai augmenté à 1.5s sur desktop, 4s sur mobile, pour laisser l'UI se rendre
      // avant de lancer le scan facial (qui sinon sature le CPU mobile)
      const inspectDelay = isMobile ? 4000 : 1500;
      const t = setTimeout(() => handleInspect(true), inspectDelay);
      return () => clearTimeout(t);
    }
  }, [loading, photos.length, handleInspect, isMobile]);

  // Rendu progressif : ajoute des chunks de vignettes par lots pour éviter de
  // monter 124 composants d'un coup sur mobile (cause du moulinage)
  useEffect(() => {
    if (loading || photos.length === 0) return;
    if (renderCount >= photos.length) return;
    const t = setTimeout(() => {
      setRenderCount(c => Math.min(c + CHUNK_STEP, photos.length));
    }, isMobile ? 250 : 100);
    return () => clearTimeout(t);
  }, [renderCount, photos.length, loading, isMobile, CHUNK_STEP]);

  // ── IGNORE FACE: remove unknown face group ──
  const ignoreFace = useCallback(async (groupId) => {
    try {
      await axios.post(`${API}/faces/ignore`, { group_id: groupId }, { headers: { Authorization: `Bearer ${token}` } });
      setFacesResult(prev => {
        if (!prev) return prev;
        const newGroups = prev.groups.filter(g => g.group_id !== groupId);
        return { ...prev, groups: newGroups, total_faces: prev.total_faces - 1 };
      });
    } catch {}
  }, [token]);

  // ── SEAL: always show modal with EMPTY field ──
  const openSealModal = useCallback((photoIds = null, suggestedName = null) => {
    setSealPhotoIds(photoIds);
    setSealEcrinName('');
    setSealSuggestion(suggestedName);
    setShowSealModal(true);
  }, []);

  // ── PRE-SEAL CHECK: detect duplicates in Coffre ──
  const checkAndSeal = useCallback(async (withEcrin) => {
    const ids = sealPhotoIds || photos.filter(p => !excludedIds.has(p.id)).map(p => p.id);
    setCheckingDoublons(true);
    try {
      const checkRes = await axios.post(`${API}/sas/check-doublons-coffre`, { photo_ids: ids }, { headers });
      if (checkRes.data.count > 0) {
        setCoffreDuplicates({ duplicates: checkRes.data.duplicates, withEcrin });
        setCheckingDoublons(false);
        return; // Don't seal yet — show warning
      }
    } catch {} // If check fails, proceed anyway
    setCheckingDoublons(false);
    if (withEcrin) executeSealDirect(ids, sealEcrinName.trim());
    else executeSealDirect(ids, '');
  }, [sealPhotoIds, photos, excludedIds, token, sealEcrinName]);

  const executeSealDirect = useCallback(async (ids, ecrinName) => {
    setSealing(true);
    try {
      const res = await axios.post(`${API}/sas/trier-lot`, {
        photo_ids: ids,
        action: 'sceller',
        ecrin_name: ecrinName,
      }, { headers });
      const count = res.data?.count || ids.length;
      sounds?.cristal?.();
      toast.success(`${count} Tr\u00e9sor${count > 1 ? 's' : ''} scell\u00e9${count > 1 ? 's' : ''} dans le Coffre${ecrinName ? ` — \u00c9crin "${ecrinName}"` : ''}`);
      if (onRefreshAfterSeal) await onRefreshAfterSeal(count, ecrinName || null);
      else await onToutSceller();
    } catch {}
    setSealing(false);
    setShowSealModal(false);
    setSealEcrinName('');
    setSealPhotoIds(null);
    setCoffreDuplicates(null);
  }, [token, onToutSceller, onRefreshAfterSeal, sounds]);

  const executeSeal = useCallback(async () => {
    await checkAndSeal(true);
  }, [checkAndSeal]);

  const executeSealWithoutEcrin = useCallback(async () => {
    await checkAndSeal(false);
  }, [checkAndSeal]);

  // ── DELETE DOUBLON ──
  const handleDeleteDoublon = useCallback(async (photoId) => {
    try {
      await axios.post(`${API}/doublons/supprimer`, { photo_ids: [photoId] }, { headers });
      setDoublonsResult(prev => {
        if (!prev?.groups) return prev;
        const newGroups = prev.groups.map(g => ({
          ...g, duplicates: g.duplicates.filter(d => d.id !== photoId),
        })).filter(g => g.duplicates.length > 0);
        return { ...prev, groups: newGroups, total_duplicates: newGroups.reduce((s, g) => s + g.duplicates.length, 0) };
      });
      loadPhotos();
    } catch {}
  }, [token, loadPhotos]);

  // ── NAME GROUP ──
  const handleNameGroup = useCallback(async (groupId) => {
    if (!nameInput.trim()) return;
    try {
      await axios.post(`${API}/faces/name`, { group_id: groupId, name: nameInput.trim() }, { headers });
      setFacesResult(prev => ({
        ...prev,
        groups: prev.groups.map(g => g.group_id === groupId ? { ...g, name: nameInput.trim() } : g),
      }));
      setNamingGroup(null);
      setNameInput('');
    } catch {}
  }, [token, nameInput]);

  // ── MERGE GROUPS ──
  const toggleMergeSelect = useCallback((groupId) => {
    setMergeSelected(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) next.delete(groupId);
      else next.add(groupId);
      return next;
    });
  }, []);

  const handleMerge = useCallback(async () => {
    if (mergeSelected.size < 2) return;
    setMerging(true);
    try {
      const groupIds = [...mergeSelected];
      await axios.post(`${API}/faces/merge`, { group_ids: groupIds }, { headers });
      // Refresh faces
      const groupRes = await axios.get(`${API}/faces/groups?status=sas`, { headers });
      setFacesResult(groupRes.data);
      setMergeMode(false);
      setMergeSelected(new Set());
    } catch {}
    setMerging(false);
  }, [mergeSelected, token]);

  if (loading) return <LoadingView text="Chargement du Sas..." />;

  const handleManualLink = async (photoId, groupId, personName) => {
    setLinkingInProgress(true);
    try {
      const payload = { photo_id: photoId };
      if (groupId) payload.group_id = groupId;
      if (personName) payload.person_name = personName;
      const res = await axios.post(`${API}/faces/manual-link`, payload, { headers });
      if (res.data.status === 'success' || res.data.status === 'already_linked') {
        toast.success(`Photo liée à ${res.data.name || personName}`, {
          style: { fontFamily: serif, backgroundColor: '#0a0a0a', border: '1px solid rgba(212,175,55,0.3)', color: gold },
        });
        // Refresh faces
        try {
          const groupRes = await axios.get(`${API}/faces/groups?status=sas`, { headers });
          setFacesResult(groupRes.data);
        } catch {}
        // Refresh photo list to show updated face_names
        loadPhotos();
      }
    } catch (e) {
      toast.error('Erreur lors de la liaison');
    }
    setLinkingInProgress(false);
    setLinkingPhotoId(null);
    setNewPersonName('');
  };

  if (loading) {
    return (
      <div data-testid="sas-loading" style={{ textAlign: 'center', padding: '80px 20px' }}>
        <div style={{
          width: 36, height: 36, borderRadius: '50%',
          border: '3px solid rgba(212,175,55,0.2)', borderTopColor: gold,
          margin: '0 auto 18px', animation: 'spin 1s linear infinite',
        }} />
        <p style={{ fontFamily: serif, fontSize: 14, color: ivory, fontStyle: 'italic' }}>Le fil se d{'\u00e9'}roule…</p>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (loadError) {
    return (
      <div data-testid="sas-error" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p style={{ fontFamily: serif, fontSize: 16, color: gold, marginBottom: 8 }}>Le Sas n'a pas pu s'ouvrir</p>
        <p style={{ fontFamily: serif, fontSize: 13, color: ivory, marginBottom: 20, lineHeight: 1.6 }}>
          Vos Tr{'\u00e9'}sors sont bien l{'\u00e0'}, mais la connexion a {'\u00e9'}t{'\u00e9'} interrompue.<br />R{'\u00e9'}essayez dans un instant.
        </p>
        <button data-testid="sas-error-retry" onClick={loadPhotos} style={{ ...secondaryBtnStyle, marginRight: 8 }}>R{'\u00e9'}essayer</button>
        <button data-testid="sas-error-close" onClick={onClose} style={secondaryBtnStyle}>Retour</button>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div data-testid="sas-empty" style={{ textAlign: 'center', padding: '60px 20px' }}>
        <p style={{ fontFamily: serif, fontSize: 18, color: gold, marginBottom: 8 }}>Votre Sas est vide</p>
        <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0' }}>Aucun Tr{'\u00e9'}sor en attente.</p>
        <button onClick={onClose} data-testid="sas-empty-close" style={secondaryBtnStyle}>Retour</button>
      </div>
    );
  }

  return (
    <div data-testid="sas-overview" style={{ padding: '20px 16px', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <p style={{ fontFamily: serif, fontSize: 15, color: '#FAF3E0', lineHeight: 1.7 }}>
          <span style={{ color: gold, fontWeight: 600, fontSize: 22 }}>{photos.length}</span>
          {' '}Tr{'\u00e9'}sor{photos.length > 1 ? 's' : ''} en attente
        </p>
        {/* Scan progress bar */}
        {scanProgress && scanProgress.scanned < scanProgress.total && (
          <div data-testid="scan-progress" style={{ marginTop: 12, maxWidth: 300, margin: '12px auto 0' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ fontFamily: serif, fontSize: 10, color: gold }}>Reconnaissance faciale</span>
              <span style={{ fontFamily: serif, fontSize: 10, color: '#FAF3E0' }}>{scanProgress.scanned}/{scanProgress.total}</span>
            </div>
            <div style={{ height: 3, backgroundColor: 'rgba(212,175,55,0.1)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', borderRadius: 2, width: `${(scanProgress.scanned / scanProgress.total) * 100}%`, transition: 'width 0.5s ease' }} />
            </div>
          </div>
        )}
        {scanProgress && scanProgress.scanned >= scanProgress.total && scanProgress.total > 0 && (
          <p style={{ fontFamily: serif, fontSize: 10, color: gold, marginTop: 8, fontStyle: 'italic' }}>
            Reconnaissance termin{'\u00e9'}e
          </p>
        )}
      </div>

      {/* Primary actions */}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', marginBottom: 28, flexWrap: 'wrap' }}>
        <button data-testid="sas-inspect-btn" onClick={handleInspect} disabled={false}
          style={{ ...goldOutlineBtn }}>
          {facesLoading ? <><Spinner size={16} /> Analyse en cours...</> : (
            <>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
              Inspecter mes Tr{'\u00e9'}sors
            </>
          )}
        </button>
        <button data-testid="sas-tout-sceller-btn" onClick={() => openSealModal(null)} disabled={sealing}
          style={{ ...goldFilledBtn, opacity: sealing ? 0.6 : 1 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          Tout sceller au Coffre-fort
        </button>
        <button data-testid="sas-tri-express-btn" onClick={() => setTriExpressOpen(true)} disabled={photos.length === 0}
          style={{ ...goldOutlineBtn, opacity: photos.length === 0 ? 0.4 : 1 }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
          Tri Express
        </button>
        <button data-testid="sas-select-mode-btn" onClick={() => { setSelectMode(!selectMode); setSelectedIds(new Set()); }}
          style={{ ...goldOutlineBtn, backgroundColor: selectMode ? 'rgba(212,175,55,0.1)' : 'transparent' }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11"/></svg>
          {selectMode ? 'Annuler' : 'Choisir et sceller'}
        </button>
      </div>

      {/* ═══ SEAL MODAL ═══ */}
      {showSealModal && (
        <div data-testid="seal-modal-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => { if (!sealing) setShowSealModal(false); }}>
          <div data-testid="seal-modal" onClick={e => e.stopPropagation()} style={{ background: '#0a0a0a', border: `1.5px solid rgba(212,175,55,0.3)`, borderRadius: 16, padding: '36px 32px', maxWidth: 440, width: '100%', textAlign: 'center', boxShadow: '0 8px 60px rgba(0,0,0,0.6)' }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke={gold} strokeWidth="1.5" strokeLinecap="round" style={{ margin: '0 auto 16px', display: 'block' }}><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            <p style={{ fontFamily: serif, fontSize: 18, color: gold, marginBottom: 6, fontWeight: 400 }}>
              Sceller {sealPhotoIds ? sealPhotoIds.length : photos.filter(p => !excludedIds.has(p.id)).length} Tr{'\u00e9'}sor{(sealPhotoIds ? sealPhotoIds.length : photos.filter(p => !excludedIds.has(p.id)).length) > 1 ? 's' : ''}
            </p>
            <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', marginBottom: 24, lineHeight: 1.6 }}>
              Quel nom pour cet {'\u00c9'}crin ?
            </p>
            <input
              data-testid="seal-ecrin-name-input"
              value={sealEcrinName}
              onChange={e => setSealEcrinName(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && sealEcrinName.trim()) executeSeal(); }}
              placeholder={sealSuggestion ? `Suggestion : ${sealSuggestion}` : "Ex : Mes Filles, Vacances 2024..."}
              autoFocus
              style={{ fontFamily: serif, fontSize: 15, padding: '12px 18px', borderRadius: 10, border: `1.5px solid ${gold}`, background: 'rgba(0,0,0,0.5)', color: ivory, outline: 'none', width: '100%', textAlign: 'center', marginBottom: 20, letterSpacing: 0.3 }}
            />
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
              <button data-testid="seal-confirm-btn" onClick={executeSeal} disabled={sealing || checkingDoublons || !sealEcrinName.trim()}
                style={{ ...goldFilledBtn, fontSize: 14, padding: '12px 32px', opacity: (sealing || checkingDoublons || !sealEcrinName.trim()) ? 0.5 : 1 }}>
                {checkingDoublons ? <><Spinner size={14} dark /> V{'\u00e9'}rification...</> : sealing ? <><Spinner size={14} dark /> Scellement...</> : `Sceller dans cet \u00c9crin`}
              </button>
              <button data-testid="seal-without-ecrin-btn" onClick={executeSealWithoutEcrin} disabled={sealing || checkingDoublons}
                style={{ ...secondaryBtnStyle, fontSize: 12, padding: '10px 20px', opacity: (sealing || checkingDoublons) ? 0.5 : 1 }}>
                {checkingDoublons ? 'V\u00e9rification...' : `Sceller sans \u00c9crin`}
              </button>
            </div>
            <button data-testid="seal-cancel-btn" onClick={() => setShowSealModal(false)} disabled={sealing}
              style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', backgroundColor: 'transparent',
                border: 'none', cursor: 'pointer', marginTop: 16, padding: '6px 16px',
                transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = '#FAF3E0'}
              onMouseLeave={e => e.currentTarget.style.color = '#FAF3E0'}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* ═══ COFFRE DUPLICATE ALERT ═══ */}
      {coffreDuplicates && (
        <div data-testid="coffre-doublon-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10001, background: 'rgba(0,0,0,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => setCoffreDuplicates(null)}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0a0a0a', border: `1.5px solid rgba(245,158,11,0.4)`, borderRadius: 16, padding: '28px 24px', maxWidth: 460, width: '100%', boxShadow: '0 8px 60px rgba(0,0,0,0.7)' }}>
            <div style={{ textAlign: 'center', marginBottom: 20 }}>
              <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="1.5" style={{ margin: '0 auto 12px', display: 'block' }}>
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" strokeLinecap="round" strokeLinejoin="round"/>
                <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
              <p style={{ fontFamily: serif, fontSize: 17, color: '#f59e0b', marginBottom: 4 }}>
                Doublons d{'\u00e9'}tect{'\u00e9'}s dans le Coffre
              </p>
              <p style={{ fontFamily: serif, fontSize: 12, color: ivory, lineHeight: 1.5 }}>
                {coffreDuplicates.duplicates.length} Tr{'\u00e9'}sor{coffreDuplicates.duplicates.length > 1 ? 's' : ''} du SAS {coffreDuplicates.duplicates.length > 1 ? 'existent' : 'existe'} d{'\u00e9'}j{'\u00e0'} dans votre Coffre-fort.
              </p>
            </div>

            <div style={{ maxHeight: 200, overflowY: 'auto', marginBottom: 20, borderRadius: 10, border: '1px solid rgba(212,175,55,0.08)', padding: '8px 12px' }}>
              {coffreDuplicates.duplicates.map((d, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0', borderBottom: i < coffreDuplicates.duplicates.length - 1 ? '1px solid rgba(212,175,55,0.06)' : 'none' }}>
                  <div style={{ width: 36, height: 36, borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(245,158,11,0.3)', flexShrink: 0 }}>
                    <img src={`${API}/photos/${d.sas_photo_id}/thumbnail?token=${token}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                  <span style={{ fontFamily: serif, fontSize: 11, color: ivory, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.sas_filename || 'Photo'}
                  </span>
                  <span style={{ fontFamily: serif, fontSize: 9, color: ivory }}>d{'\u00e9'}j{'\u00e0'} au Coffre</span>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'center' }}>
              <button data-testid="coffre-doublon-delete-btn"
                onClick={async () => {
                  const dupIds = coffreDuplicates.duplicates.map(d => d.sas_photo_id);
                  try {
                    await axios.post(`${API}/doublons/supprimer`, { photo_ids: dupIds }, { headers });
                    setPhotos(prev => prev.filter(p => !dupIds.includes(p.id)));
                    toast.success(`${dupIds.length} doublon${dupIds.length > 1 ? 's' : ''} retir\u00e9${dupIds.length > 1 ? 's' : ''} du SAS`);
                  } catch {}
                  // Seal the remaining photos
                  const allIds = sealPhotoIds || photos.filter(p => !excludedIds.has(p.id)).map(p => p.id);
                  const cleanIds = allIds.filter(id => !dupIds.includes(id));
                  setCoffreDuplicates(null);
                  if (cleanIds.length > 0) executeSealDirect(cleanIds, coffreDuplicates.withEcrin ? sealEcrinName.trim() : '');
                  else { setShowSealModal(false); toast('Aucun Tr\u00e9sor \u00e0 sceller apr\u00e8s retrait des doublons.'); }
                }}
                style={{ ...goldFilledBtn, fontSize: 13, padding: '10px 28px', width: '100%', justifyContent: 'center' }}>
                Supprimer les doublons et sceller le reste
              </button>
              <button data-testid="coffre-doublon-ignore-btn"
                onClick={() => {
                  const allIds = sealPhotoIds || photos.filter(p => !excludedIds.has(p.id)).map(p => p.id);
                  setCoffreDuplicates(null);
                  executeSealDirect(allIds, coffreDuplicates.withEcrin ? sealEcrinName.trim() : '');
                }}
                style={{ ...secondaryBtnStyle, fontSize: 12, padding: '8px 24px' }}>
                Ignorer et tout sceller
              </button>
              <button onClick={() => setCoffreDuplicates(null)}
                style={{ fontFamily: serif, fontSize: 11, color: ivory, backgroundColor: 'transparent', border: 'none', cursor: 'pointer', padding: '4px 16px' }}>
                Annuler le scellement
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ═══ DOUBLONS RESULT ═══ */}
      {doublonsResult && doublonsResult.status !== 'error' && (
        <Section title="Doublons" testId="doublons-result">
          {doublonsResult.total_duplicates === 0 ? (
            <p style={resultTextStyle}>
              <CheckIcon /> Z{'\u00e9'}ro doublon — vos {doublonsResult.photos_scanned} Tr{'\u00e9'}sors (Sas + Coffre) sont tous uniques.
            </p>
          ) : (
            <>
              <p style={{ ...resultTextStyle, marginBottom: 16 }}>
                <WarnIcon /> {doublonsResult.total_duplicates} doublon(s) d{'\u00e9'}tect{'\u00e9'}(s)
              </p>
              <button data-testid="delete-all-doublons-btn"
                onClick={async () => {
                  const allDupIds = doublonsResult.groups.flatMap(g => g.duplicates.map(d => d.id));
                  if (!allDupIds.length) return;
                  try {
                    await axios.post(`${API}/doublons/supprimer`, { photo_ids: allDupIds }, { headers });
                    setDoublonsResult(prev => ({ ...prev, groups: [], total_duplicates: 0 }));
                    toast.success(`${allDupIds.length} doublon${allDupIds.length > 1 ? 's' : ''} supprim\u00e9${allDupIds.length > 1 ? 's' : ''}`);
                    loadPhotos();
                  } catch { toast.error('Erreur lors de la suppression'); }
                }}
                style={{ ...goldFilledBtn, fontSize: 13, padding: '10px 24px', marginBottom: 20, width: '100%', justifyContent: 'center' }}>
                Supprimer tous les doublons ({doublonsResult.total_duplicates})
              </button>
              {doublonsResult.groups?.map((group, gi) => (
                <div key={gi} style={comparisonBoxStyle}>
                  <p style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', marginBottom: 10 }}>
                    Comparaison {gi + 1}
                  </p>
                  <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap', justifyContent: 'center' }}>
                    <CompareCard photo={group.original} token={token} label="Original" borderColor="#D4AF37" />
                    <div style={{ display: 'flex', alignItems: 'center', color: '#D4AF37', fontSize: 20, padding: '20px 0', fontFamily: serif }}>VS</div>
                    {group.duplicates.map((dup, di) => (
                      <CompareCard key={di} photo={dup} token={token}
                        label={`Copie (identique ${dup.distance === 0 ? '100%' : `\u00e0 ${Math.round((1 - dup.distance / 16) * 100)}%`})`}
                        borderColor="#f59e0b"
                        onDelete={() => handleDeleteDoublon(dup.id)} />
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </Section>
      )}

      {/* ═══ FACES LOADING ═══ */}
      {facesLoading && (
        <Section title="Reconnaissance faciale" testId="faces-loading">
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Spinner size={16} />
            <span style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0' }}>Analyse des visages en cours...</span>
          </div>
        </Section>
      )}

      {/* ═══ FACES RESULT ═══ */}
      {facesResult && facesResult.status !== 'error' && (
        <Section title="Visages reconnus" testId="faces-result">
          {(!facesResult.groups || facesResult.groups.length === 0) ? (
            <p style={resultTextStyle}>Aucun groupe de visages identifi{'\u00e9'}.</p>
          ) : (
            <>
              {facesResult.groups.some(g => g.name) && (
                <div data-testid="auto-recognition-banner" style={{ padding: '12px 16px', borderRadius: 10, background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.15)', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontSize: 18 }}>{'\u2728'}</span>
                  <p style={{ fontFamily: serif, fontSize: 13, color: '#FAF3E0', margin: 0 }}>
                    Bobine reconna{'\u00ee'}t : <strong style={{ color: gold }}>{facesResult.groups.filter(g => g.name).map(g => g.name).join(', ')}</strong>
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <p style={{ ...resultTextStyle, margin: 0 }}>
                  <CheckIcon /> {facesResult.groups.length} personne{facesResult.groups.length > 1 ? 's' : ''} identifi{'\u00e9'}e{facesResult.groups.length > 1 ? 's' : ''}
                </p>
                {facesResult.groups.length >= 2 && (
                  <button data-testid="merge-toggle-btn" onClick={() => { setMergeMode(!mergeMode); setMergeSelected(new Set()); }}
                    style={{ fontFamily: serif, fontSize: 11, padding: '5px 14px', borderRadius: 6, cursor: 'pointer', color: mergeMode ? ivory : gold, background: mergeMode ? '#D4AF37' : 'transparent', border: `1px solid rgba(212,175,55,0.25)`, transition: 'all 0.2s' }}>
                    {mergeMode ? 'Annuler la fusion' : 'Fusionner des identit\u00e9s'}
                  </button>
                )}
              </div>

              {/* Merge bar */}
              {mergeMode && mergeSelected.size >= 2 && (
                <div data-testid="merge-bar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginBottom: 14, padding: '10px 16px', borderRadius: 10, background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.2)' }}>
                  <p style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0', margin: 0 }}>
                    {mergeSelected.size} lots s{'\u00e9'}lectionn{'\u00e9'}s
                  </p>
                  <button data-testid="merge-confirm-btn" onClick={handleMerge} disabled={merging}
                    style={{ fontFamily: serif, fontSize: 12, padding: '6px 18px', borderRadius: 6, cursor: 'pointer', background: gold, color: '#000', border: 'none', fontWeight: 600, opacity: merging ? 0.5 : 1 }}>
                    {merging ? 'Fusion...' : 'Fusionner'}
                  </button>
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {facesResult.groups.map((group, i) => (
                  <div key={group.group_id} data-testid={`face-group-${group.group_id}`}
                    style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px', borderRadius: 12, border: mergeMode && mergeSelected.has(group.group_id) ? `2px solid ${gold}` : group.name ? '1.5px solid rgba(212,175,55,0.2)' : `1px solid rgba(212,175,55,0.12)`, background: mergeMode && mergeSelected.has(group.group_id) ? 'rgba(212,175,55,0.06)' : group.name ? 'rgba(212,175,55,0.03)' : 'rgba(212,175,55,0.02)', cursor: mergeMode ? 'pointer' : 'default', transition: 'all 0.2s' }}
                    onClick={mergeMode ? () => toggleMergeSelect(group.group_id) : undefined}>

                    {/* Merge checkbox */}
                    {mergeMode && (
                      <div style={{ width: 22, height: 22, borderRadius: 6, border: `1.5px solid ${mergeSelected.has(group.group_id) ? gold : ivoryAlpha(0.2)}`, background: mergeSelected.has(group.group_id) ? gold : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'all 0.2s' }}>
                        {mergeSelected.has(group.group_id) && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>}
                      </div>
                    )}

                    {group.sample_crop && (
                      <div style={{ width: 56, height: 56, borderRadius: '50%', overflow: 'hidden', border: `2.5px solid ${gold}`, flexShrink: 0 }}>
                        <img src={`data:image/jpeg;base64,${group.sample_crop}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      </div>
                    )}
                    <div style={{ flex: 1 }}>
                      {namingGroup === group.group_id ? (
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <input
                            data-testid={`name-input-${group.group_id}`}
                            value={nameInput}
                            onChange={e => setNameInput(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') handleNameGroup(group.group_id); }}
                            placeholder="Prénom..."
                            autoFocus
                            onClick={e => e.stopPropagation()}
                            style={{ fontFamily: serif, fontSize: 13, padding: '6px 12px', borderRadius: 8, border: `1px solid ${gold}`, background: 'rgba(0,0,0,0.5)', color: ivory, outline: 'none', width: 140 }}
                          />
                          <button onClick={e => { e.stopPropagation(); handleNameGroup(group.group_id); }}
                            style={{ fontFamily: serif, fontSize: 11, padding: '6px 14px', borderRadius: 6, background: gold, color: '#000', border: 'none', cursor: 'pointer' }}>OK</button>
                          <button onClick={e => { e.stopPropagation(); setNamingGroup(null); setNameInput(''); }}
                            style={{ fontFamily: serif, fontSize: 11, padding: '6px 10px', borderRadius: 6, background: 'transparent', color: ivory, border: `1px solid ${ivoryAlpha(0.15)}`, cursor: 'pointer' }}>Annuler</button>
                        </div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                          {group.name ? (
                            <>
                              <span style={{ fontFamily: serif, fontSize: 14, color: ivory }}>{group.name}</span>
                              <span style={{ fontFamily: serif, fontSize: 9, color: '#000', background: '#D4AF37', padding: '2px 6px', borderRadius: 4 }}>Connue</span>
                              {!mergeMode && (
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <button data-testid={`name-btn-${group.group_id}`}
                                    onClick={e => { e.stopPropagation(); setNamingGroup(group.group_id); setNameInput(group.name || ''); }}
                                    style={{ fontFamily: serif, fontSize: 10, padding: '3px 10px', borderRadius: 6, cursor: 'pointer', color: '#D4AF37', background: 'none', border: '1px solid rgba(212,175,55,0.25)' }}>
                                    Renommer
                                  </button>
                                  <button data-testid={`ignore-face-sas-${group.group_id}`}
                                    onClick={e => { e.stopPropagation(); ignoreFace(group.group_id); }}
                                    title="Supprimer cette identité"
                                    style={{
                                      width: 22, height: 22, borderRadius: '50%', cursor: 'pointer',
                                      background: 'none', border: '1px solid rgba(212,175,55,0.15)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      color: '#D4AF37', fontSize: 11, fontWeight: 300,
                                    }}>
                                    ✕
                                  </button>
                                </div>
                              )}
                            </>
                          ) : (
                            <>
                              <span style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0', fontStyle: 'italic' }}>
                                Nouveau visage {'\u00e0'} d{'\u00e9'}couvrir
                              </span>
                              {!mergeMode && (
                                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                                  <button data-testid={`name-btn-${group.group_id}`}
                                    onClick={e => { e.stopPropagation(); setNamingGroup(group.group_id); setNameInput(''); }}
                                    style={{ fontFamily: serif, fontSize: 12, padding: '5px 16px', borderRadius: 6, cursor: 'pointer', color: '#000', background: '#D4AF37', border: 'none', fontWeight: 600, letterSpacing: 0.3 }}>
                                    Reconna{'\u00ee'}tre
                                  </button>
                                  <button data-testid={`ignore-face-sas-${group.group_id}`}
                                    onClick={e => { e.stopPropagation(); ignoreFace(group.group_id); }}
                                    title="Ignorer ce visage"
                                    style={{
                                      width: 26, height: 26, borderRadius: '50%', cursor: 'pointer',
                                      background: 'none', border: '1px solid rgba(212,175,55,0.15)',
                                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                                      color: '#D4AF37', fontSize: 13, fontWeight: 300,
                                    }}>
                                    ✕
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      )}
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4 }}>
                        <span style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0' }}>
                          {group.photo_count} photo{group.photo_count > 1 ? 's' : ''}
                        </span>
                        {!mergeMode && group.photo_ids && (
                          <button data-testid={`seal-group-${group.group_id}`}
                            onClick={e => { e.stopPropagation(); openSealModal(group.photo_ids, group.name || null); }}
                            style={{ fontFamily: serif, fontSize: 10, padding: '3px 12px', borderRadius: 6, cursor: 'pointer', background: 'rgba(212,175,55,0.1)', color: gold, border: `1px solid rgba(212,175,55,0.2)`, transition: 'all 0.2s' }}>
                            Sceller ses Tr{'\u00e9'}sors
                          </button>
                        )}
                      </div>
                      {/* Mini-aperçu des photos du groupe — permet de retirer une mauvaise photo */}
                      {!mergeMode && group.photo_ids && group.photo_ids.length > 0 && (
                        <div style={{ display: 'flex', gap: 4, marginTop: 8, flexWrap: 'wrap' }}>
                          {group.photo_ids.slice(0, 6).map(pid => (
                            <div key={pid} style={{ position: 'relative', width: 44, height: 44, borderRadius: 6, overflow: 'hidden', border: '1px solid rgba(212,175,55,0.15)' }}>
                              <img
                                src={`${API}/photos/${pid}/thumbnail?token=${token}`}
                                alt=""
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                              <button
                                data-testid={`remove-photo-${pid}-from-${group.group_id}`}
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    await axios.post(`${API}/faces/remove-from-group`, { group_id: group.group_id, photo_id: pid }, { headers: { Authorization: `Bearer ${token}` } });
                                    setFacesResult(prev => {
                                      if (!prev) return prev;
                                      const newGroups = prev.groups.map(g => {
                                        if (g.group_id !== group.group_id) return g;
                                        const newIds = g.photo_ids.filter(id => id !== pid);
                                        return { ...g, photo_ids: newIds, photo_count: newIds.length };
                                      }).filter(g => g.photo_count > 0);
                                      return { ...prev, groups: newGroups };
                                    });
                                  } catch {}
                                }}
                                title="Retirer de ce groupe"
                                style={{
                                  position: 'absolute', top: 1, right: 1,
                                  width: 16, height: 16, borderRadius: '50%',
                                  background: 'rgba(0,0,0,0.7)', border: 'none',
                                  color: '#D4AF37', fontSize: 10,
                                  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                  padding: 0, lineHeight: 1,
                                }}>
                                ✕
                              </button>
                            </div>
                          ))}
                          {group.photo_ids.length > 6 && (
                            <span style={{ fontFamily: serif, fontSize: 10, color: ivory, alignSelf: 'center' }}>+{group.photo_ids.length - 6}</span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </Section>
      )}

      {/* ═══ PHOTO GRID ═══ */}
      {excludedIds.size > 0 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginTop: 16, alignItems: 'center' }}>
          <span style={{ fontFamily: serif, fontSize: 12, color: '#FAF3E0' }}>
            {excludedIds.size} photo{excludedIds.size > 1 ? 's' : ''} retirée{excludedIds.size > 1 ? 's' : ''}
          </span>
          <button onClick={() => setExcludedIds(new Set())}
            style={{ fontFamily: serif, fontSize: 11, padding: '4px 14px', borderRadius: 6, cursor: 'pointer',
              color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.2)' }}>
            Tout restaurer
          </button>
        </div>
      )}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
        gap: 8,
        marginBottom: 32,
        marginTop: 24,
      }}>
        {photos.filter(p => !excludedIds.has(p.id)).slice(0, renderCount).map((photo, idx) => (
          <div key={photo.id} style={{ position: 'relative' }}
            onClick={selectMode ? () => toggleSelectPhoto(photo.id) : undefined}>
            {selectMode && (
              <div style={{
                position: 'absolute', top: 4, left: 4, zIndex: 10,
                width: 22, height: 22, borderRadius: 6,
                backgroundColor: selectedIds.has(photo.id) ? '#D4AF37' : 'rgba(0,0,0,0.6)',
                border: selectedIds.has(photo.id) ? '2px solid #D4AF37' : '2px solid rgba(212,175,55,0.5)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                pointerEvents: 'none',
              }}>
                {selectedIds.has(photo.id) && (
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#000" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                )}
              </div>
            )}
            <SasThumb photo={photo} token={token} delay={idx * 30}
              onExclude={selectMode ? undefined : () => {
                setExcludedIds(prev => { const next = new Set(prev); next.add(photo.id); return next; });
              }}
              onLink={selectMode ? undefined : () => setLinkingPhotoId(photo.id)} />
          </div>
        ))}
      </div>
      {/* Indicateur de chargement progressif des vignettes restantes (mobile) */}
      {renderCount < photos.filter(p => !excludedIds.has(p.id)).length && (
        <div data-testid="sas-progressive-loader" style={{ textAlign: 'center', padding: '12px 0 24px' }}>
          <span style={{ fontFamily: serif, fontSize: 11, color: '#FAF3E0', opacity: 0.6 }}>
            {renderCount} / {photos.filter(p => !excludedIds.has(p.id)).length} affich{'\u00e9'}{renderCount > 1 ? 's' : ''}...
          </span>
        </div>
      )}

      {/* ═══ SELECTION BAR ═══ */}
      {selectMode && selectedIds.size > 0 && (
        <div data-testid="sas-selection-bar" style={{
          position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 9999,
          backgroundColor: 'rgba(10,10,10,0.95)', borderTop: '1px solid #D4AF37',
          padding: '14px 20px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16,
        }}>
          <span style={{ fontFamily: serif, fontSize: 13, color: '#D4AF37', fontWeight: 600 }}>
            {selectedIds.size} photo{selectedIds.size > 1 ? 's' : ''}
          </span>
          <button data-testid="sas-seal-selected-btn"
            onClick={() => {
              setSealPhotoIds([...selectedIds]);
              setShowSealModal(true);
            }}
            style={{
              fontFamily: serif, fontSize: 13, fontWeight: 600, padding: '10px 24px',
              borderRadius: 8, cursor: 'pointer', background: 'linear-gradient(145deg, #D4AF37, #B8860B)', color: '#000', border: 'none',
              display: 'inline-flex', alignItems: 'center', gap: 8,
            }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
            Sceller la s{'\u00e9'}lection
          </button>
          <button onClick={() => { setSelectMode(false); setSelectedIds(new Set()); }}
            style={{ fontFamily: serif, fontSize: 12, color: '#D4AF37', background: 'none', border: '1px solid rgba(212,175,55,0.3)', padding: '8px 16px', borderRadius: 8, cursor: 'pointer' }}>
            Annuler
          </button>
        </div>
      )}

      {/* ═══ MANUAL LINK MODAL ═══ */}
      {linkingPhotoId && (
        <div data-testid="manual-link-overlay" style={{ position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
          onClick={() => { if (!linkingInProgress) { setLinkingPhotoId(null); setNewPersonName(''); } }}>
          <div onClick={e => e.stopPropagation()} style={{ background: '#0a0a0a', border: `1.5px solid rgba(212,175,55,0.3)`, borderRadius: 16, padding: '28px 24px', maxWidth: 380, width: '100%', boxShadow: '0 8px 60px rgba(0,0,0,0.6)' }}>
            <p style={{ fontFamily: serif, fontSize: 16, color: gold, marginBottom: 4, textAlign: 'center' }}>
              Reconna{'\u00ee'}tre ce Tr{'\u00e9'}sor
            </p>
            <p style={{ fontFamily: serif, fontSize: 12, color: ivory, marginBottom: 20, textAlign: 'center', lineHeight: 1.5 }}>
              {'\u00c0'} quelle personne appartient ce Tr{'\u00e9'}sor ?
            </p>

            {/* Photo preview */}
            <div style={{ width: 80, height: 80, margin: '0 auto 20px', borderRadius: 10, overflow: 'hidden', border: `1.5px solid rgba(212,175,55,0.2)` }}>
              <img src={`${API}/photos/${linkingPhotoId}/thumbnail?token=${token}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>

            {/* Existing groups */}
            {facesResult?.groups?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <p style={{ fontFamily: serif, fontSize: 11, color: ivory, marginBottom: 8 }}>Personnes connues :</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {facesResult.groups.map(g => (
                    <button key={g.group_id} data-testid={`link-to-group-${g.group_id}`}
                      disabled={linkingInProgress}
                      onClick={() => handleManualLink(linkingPhotoId, g.group_id, null)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px',
                        borderRadius: 10, background: 'rgba(212,175,55,0.04)', border: '1px solid rgba(212,175,55,0.15)',
                        cursor: 'pointer', fontFamily: serif, fontSize: 13, color: ivory, transition: 'all 0.2s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = gold}
                      onMouseLeave={e => e.currentTarget.style.borderColor = '#D4AF37'}>
                      {g.sample_crop && (
                        <div style={{ width: 32, height: 32, borderRadius: '50%', overflow: 'hidden', border: `1.5px solid ${gold}`, flexShrink: 0 }}>
                          <img src={`data:image/jpeg;base64,${g.sample_crop}`} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        </div>
                      )}
                      <span>{g.name || 'Visage non nomm\u00e9'}</span>
                      <span style={{ fontSize: 11, color: '#000', marginLeft: 'auto', backgroundColor: gold, padding: '3px 12px', borderRadius: 6, fontWeight: 600 }}>Lier</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* New person */}
            <div style={{ borderTop: `1px solid rgba(212,175,55,0.1)`, paddingTop: 16 }}>
              <p style={{ fontFamily: serif, fontSize: 11, color: ivoryAlpha(0.5), marginBottom: 8 }}>Nouvelle personne :</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  data-testid="manual-link-new-name"
                  value={newPersonName}
                  onChange={e => setNewPersonName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && newPersonName.trim()) handleManualLink(linkingPhotoId, null, newPersonName.trim()); }}
                  placeholder="Prénom..."
                  autoFocus
                  style={{ fontFamily: serif, fontSize: 13, padding: '8px 14px', borderRadius: 8, border: `1px solid ${gold}`, background: 'rgba(0,0,0,0.5)', color: ivory, outline: 'none', flex: 1 }}
                />
                <button
                  data-testid="manual-link-create-btn"
                  disabled={!newPersonName.trim() || linkingInProgress}
                  onClick={() => handleManualLink(linkingPhotoId, null, newPersonName.trim())}
                  style={{ fontFamily: serif, fontSize: 12, padding: '8px 18px', borderRadius: 8, background: gold, color: '#000', border: 'none', cursor: 'pointer', fontWeight: 600, opacity: (!newPersonName.trim() || linkingInProgress) ? 0.5 : 1 }}>
                  {linkingInProgress ? '...' : 'Cr\u00e9er'}
                </button>
              </div>
            </div>

            <button onClick={() => { setLinkingPhotoId(null); setNewPersonName(''); }} disabled={linkingInProgress}
              style={{ fontFamily: serif, fontSize: 12, color: ivoryAlpha(0.4), backgroundColor: 'transparent', border: 'none', cursor: 'pointer', marginTop: 16, padding: '6px 16px', width: '100%', textAlign: 'center' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {/* Secondary */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: 16, flexWrap: 'wrap', paddingBottom: 40, flexDirection: 'column', alignItems: 'center' }}>
        <button data-testid="sas-tout-sceller-bottom-btn" onClick={() => openSealModal(null)} disabled={sealing}
          style={{ ...goldFilledBtn, fontSize: 14, padding: '12px 32px', opacity: sealing ? 0.5 : 1 }}>
          Sceller {photos.filter(p => !excludedIds.has(p.id)).length} Tr{'\u00e9'}sor{photos.filter(p => !excludedIds.has(p.id)).length > 1 ? 's' : ''} au Coffre-fort
        </button>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center' }}>
          <button data-testid="sas-smart-tamis-btn" onClick={onSmartTamis} style={secondaryBtnStyle}>Tri intelligent (IA)</button>
          <button data-testid="sas-tri-manuel-btn" onClick={onTriManuel} style={{ ...secondaryBtnStyle, color: '#FAF3E0', borderColor: '#FAF3E0' }}>Tri photo par photo</button>
          <button data-testid="sas-tri-express-bottom-btn" onClick={() => setTriExpressOpen(true)} disabled={photos.length === 0}
            style={{ ...secondaryBtnStyle, opacity: photos.length === 0 ? 0.4 : 1 }}>
            Tri Express (swipe)
          </button>
        </div>
      </div>

      {/* Mode Tri Express */}
      {triExpressOpen && (
        <TriExpressMode
          photos={photos.filter(p => !excludedIds.has(p.id))}
          albums={albums}
          token={token}
          onClose={() => { setTriExpressOpen(false); onRefreshAfterSeal && onRefreshAfterSeal(); }}
          onRefresh={() => { onRefreshAfterSeal && onRefreshAfterSeal(); }}
          onAlbumPhotoAdded={(albumId, photoId) => {
            // Mise à jour locale immédiate dans le state SasOverview
            setAlbums(prev => prev.map(a => a.id === albumId
              ? { ...a, photo_count: (a.photo_count || 0) + 1, photo_ids: [...(a.photo_ids || []), photoId] }
              : a));
            // Propage au Dashboard parent pour que les vignettes d'Écrin se rafraîchissent
            if (onAlbumPhotoAdded) onAlbumPhotoAdded(albumId, photoId);
          }}
        />
      )}
    </div>
  );
};

/* ── Micro-components ── */

const Spinner = ({ size = 16, dark = false }) => (
  <div style={{
    width: size, height: size,
    border: `2px solid ${dark ? 'rgba(0,0,0,0.15)' : 'rgba(212,175,55,0.15)'}`,
    borderTop: `2px solid ${dark ? '#000' : gold}`,
    borderRadius: '50%', animation: 'saspin 0.8s linear infinite',
    display: 'inline-block', flexShrink: 0,
  }} />
);

const CheckIcon = () => <span style={{ color: '#D4AF37', fontSize: 16, flexShrink: 0 }}>{'\u2713'}</span>;
const WarnIcon = () => <span style={{ color: '#f59e0b', fontSize: 16, flexShrink: 0 }}>{'\u26a0'}</span>;

const Section = ({ title, children, testId }) => (
  <div data-testid={testId} style={{ margin: '0 auto 20px', padding: '18px 20px', borderRadius: 14, maxWidth: 700, background: 'rgba(212,175,55,0.02)', border: '1px solid rgba(212,175,55,0.08)' }}>
    <p style={{ fontFamily: serif, fontSize: 11, color: '#D4AF37', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 12 }}>{title}</p>
    {children}
  </div>
);

const LoadingView = ({ text }) => (
  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
    <Spinner size={32} />
    <p style={{ fontFamily: serif, fontSize: 14, color: '#FAF3E0', marginTop: 16 }}>{text}</p>
    <style>{`@keyframes saspin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

const CompareCard = ({ photo, token, label, borderColor, onDelete }) => {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
      <div style={{ width: 140, height: 140, borderRadius: 10, overflow: 'hidden', border: `2.5px solid ${borderColor}`, background: 'rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        {!loaded && !failed && <span style={{ fontFamily: serif, fontSize: 9, color: '#D4AF37' }}>Chargement...</span>}
        {failed && <span style={{ fontFamily: serif, fontSize: 9, color: '#FAF3E0', textAlign: 'center', padding: 8 }}>{photo.filename?.slice(0, 15) || 'Photo'}</span>}
        <img src={`${API}/photos/${photo.id}/thumbnail?token=${token}`} alt=""
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none' }}
          onLoad={() => setLoaded(true)}
          onError={e => {
            if (!e.target.dataset.r) { e.target.dataset.r = '1'; e.target.src = `${API}/photos/${photo.id}/image?token=${token}`; }
            else setFailed(true);
          }} />
      </div>
      <span style={{ fontFamily: serif, fontSize: 11, color: borderColor, textAlign: 'center' }}>{label}</span>
      <span style={{ fontFamily: serif, fontSize: 10, color: '#FAF3E0', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'center' }}>
        {photo.filename}
      </span>
      {onDelete && (
        <button data-testid={`delete-doublon-${photo.id}`} onClick={onDelete}
          style={{ fontFamily: serif, fontSize: 11, padding: '5px 16px', borderRadius: 8, cursor: 'pointer', color: '#D4AF37', backgroundColor: 'transparent', border: '1px solid rgba(212,175,55,0.3)', transition: 'all 0.2s' }}>
          Retirer ce doublon
        </button>
      )}
    </div>
  );
};

const SasThumb = React.memo(({ photo, token, delay, onExclude, onLink }) => {
  const [loaded, setLoaded] = useState(false);
  const [src, setSrc] = useState(null);
  const [visible, setVisible] = useState(false);
  const ref = React.useRef(null);

  // Lazy loading: only load when visible in viewport
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) { setVisible(true); observer.disconnect(); }
    }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!visible) return;
    const timer = setTimeout(() => { setSrc(`${API}/photos/${photo.id}/thumbnail?token=${token}`); }, Math.min(delay, 200));
    return () => clearTimeout(timer);
  }, [photo.id, token, delay, visible]);

  return (
    <div ref={ref} data-testid={`sas-thumb-${photo.id}`}
      style={{ position: 'relative', paddingBottom: '100%', borderRadius: 8, overflow: 'hidden', border: '1px solid rgba(212,175,55,0.15)', backgroundColor: 'rgba(212,175,55,0.03)' }}>
      {!loaded && <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Spinner size={16} /></div>}
      {src && <img src={src} alt="" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: loaded ? 'block' : 'none' }}
        onLoad={() => setLoaded(true)}
        onError={e => { if (!e.target.dataset.r) { e.target.dataset.r = '1'; setTimeout(() => { e.target.src = `${API}/photos/${photo.id}/image?token=${token}`; }, 1000); } }} />}
      {loaded && (
        <div style={{ position: 'absolute', bottom: 3, left: 3, right: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', zIndex: 5 }}>
          {onLink && !(photo.face_names && photo.face_names.length > 0) && (
            <button data-testid={`sas-identify-${photo.id}`}
              onClick={(e) => { e.stopPropagation(); onLink(); }}
              style={{
                padding: '4px 10px', borderRadius: 6, cursor: 'pointer',
                background: 'linear-gradient(145deg, #D4AF37, #B8860B)', border: 'none',
                fontFamily: serif, fontSize: 9, fontWeight: 600, color: '#000', lineHeight: 1,
                letterSpacing: 0.3,
              }}
            >Reconna{'\u00ee'}tre</button>
          )}
          {onExclude && (
            <button data-testid={`sas-exclude-${photo.id}`}
              onClick={(e) => { e.stopPropagation(); onExclude(); }}
              title="Retirer du scellement"
              style={{
                width: 20, height: 20, borderRadius: '50%', cursor: 'pointer',
                backgroundColor: 'rgba(0,0,0,0.7)', border: '1px solid rgba(212,175,55,0.4)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#FAF3E0', fontSize: 10, lineHeight: 1, padding: 0,
                marginLeft: 'auto',
              }}
            >&#x2715;</button>
          )}
        </div>
      )}
      {/* Named badge — clickable to correct */}
      {loaded && photo.face_names && photo.face_names.length > 0 && (
        <div style={{ position: 'absolute', bottom: 3, left: 3, zIndex: 6 }}>
          <button
            data-testid={`sas-rename-${photo.id}`}
            onClick={(e) => { e.stopPropagation(); if (onLink) onLink(); }}
            style={{
              fontFamily: serif, fontSize: 8, color: '#000', background: 'linear-gradient(145deg, #D4AF37, #B8860B)',
              padding: '2px 7px', borderRadius: 4, fontWeight: 600, letterSpacing: 0.3,
              border: 'none', cursor: 'pointer',
            }}
          >
            {photo.face_names.join(' & ')}
          </button>
        </div>
      )}
    </div>
  );
}, (prev, next) => prev.photo.id === next.photo.id
  && prev.token === next.token
  && prev.delay === next.delay
  && prev.photo.face_names === next.photo.face_names
  && Boolean(prev.onExclude) === Boolean(next.onExclude)
  && Boolean(prev.onLink) === Boolean(next.onLink));

/* ── Styles ── */

const goldOutlineBtn = {
  fontFamily: serif, fontSize: 14, fontWeight: 500, padding: '12px 28px',
  borderRadius: 10, cursor: 'pointer', backgroundColor: 'transparent', color: gold,
  border: `1.5px solid ${gold}`, display: 'inline-flex', alignItems: 'center', gap: 10,
  transition: 'all 0.3s', letterSpacing: 0.3,
};

const goldFilledBtn = {
  fontFamily: serif, fontSize: 14, fontWeight: 600, padding: '12px 28px',
  borderRadius: 10, cursor: 'pointer',
  background: 'radial-gradient(ellipse 65% 85% at 30% 50%, #FFF8C9 0%, rgba(255, 248, 201, 0.45) 22%, transparent 58%), linear-gradient(135deg, #D6BB6A 0%, #C9A632 30%, #A8830C 65%, #6B4F0A 100%)',
  color: '#1a1100',
  border: '1px solid #8B6914',
  boxShadow: 'inset 0 1px 0 rgba(255, 248, 201, 0.35), inset 0 -2px 4px rgba(40, 26, 0, 0.5), 0 2px 8px rgba(0, 0, 0, 0.4)',
  textShadow: '0 1px 0 rgba(255, 248, 201, 0.22)',
  letterSpacing: 0.3, transition: 'all 0.3s',
  display: 'inline-flex', alignItems: 'center', gap: 10,
};

const secondaryBtnStyle = {
  fontFamily: serif, fontSize: 13, padding: '10px 24px', borderRadius: 8,
  cursor: 'pointer', color: gold, backgroundColor: 'transparent',
  border: '1px solid rgba(212,175,55,0.25)', transition: 'all 0.3s',
};

const resultTextStyle = {
  fontFamily: serif, fontSize: 13, color: '#FAF3E0',
  display: 'flex', alignItems: 'center', gap: 8,
};

const comparisonBoxStyle = {
  padding: 16, borderRadius: 12, border: '1px solid rgba(212,175,55,0.08)',
  background: 'rgba(0,0,0,0.2)', marginBottom: 12,
};

export default SasOverview;

